import React, { useState } from 'react'
import { useAuth } from '../context/AuthContext'

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #082828 0%, #0f5454 60%, #148080 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    padding: '20px',
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '44px 40px 36px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '32px',
  },
  divider: {
    height: '1px',
    background: '#e2e8f0',
    margin: '0 0 28px',
  },
  heading: {
    fontSize: '17px',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: '4px',
  },
  subheading: {
    fontSize: '13px',
    color: '#64748b',
    marginBottom: '24px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '12.5px',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '6px',
    letterSpacing: '0.2px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1.5px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#0f172a',
    background: '#f8fafc',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box',
  },
  inputFocus: {
    borderColor: '#19a8a8',
    boxShadow: '0 0 0 3px rgba(25,168,168,0.12)',
    background: '#fff',
  },
  forgotRow: {
    textAlign: 'right',
    marginTop: '-8px',
    marginBottom: '22px',
  },
  forgotLink: {
    fontSize: '12.5px',
    color: '#148080',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'inherit',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
  button: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #148080, #19a8a8)',
    color: '#fff',
    border: 'none',
    borderRadius: '9px',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.2px',
    boxShadow: '0 4px 14px rgba(20,128,128,0.35)',
    transition: 'opacity 0.15s, transform 0.1s',
  },
  buttonDisabled: {
    opacity: 0.65,
    cursor: 'not-allowed',
  },
  error: {
    background: '#fff1f2',
    border: '1px solid #fecdd3',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    color: '#be123c',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  success: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    color: '#166534',
    marginBottom: '16px',
  },
  footer: {
    marginTop: '24px',
    textAlign: 'center',
    fontSize: '11.5px',
    color: '#94a3b8',
  },
}

export default function Login() {
  const { login, resetPassword } = useAuth()

  const [email, setEmail]          = useState('')
  const [password, setPassword]    = useState('')
  const [loading, setLoading]      = useState(false)
  const [error, setError]          = useState('')
  const [resetSent, setResetSent]  = useState(false)
  const [focusedField, setFocused] = useState(null)

  async function handleLogin(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password)
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleForgotPassword() {
    if (!email) {
      setError('Enter your email address first, then click "Forgot password".')
      return
    }
    setError('')
    setLoading(true)
    try {
      await resetPassword(email)
      setResetSent(true)
    } catch (err) {
      setError(err.message || 'Could not send reset email.')
    } finally {
      setLoading(false)
    }
  }

  function inputStyle(field) {
    return focusedField === field
      ? { ...styles.input, ...styles.inputFocus }
      : styles.input
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{width:'100%', textAlign:'center', marginBottom:'28px', paddingTop:'20px'}}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 125" style={{height:'130px', display:'inline-block'}}>
            <ellipse cx="100" cy="32" rx="14" ry="26" fill="#1A6B5E" transform="rotate(-18 100 32)"/>
            <ellipse cx="83" cy="60" rx="14" ry="26" fill="#2D9E8F" transform="rotate(54 83 60)"/>
            <ellipse cx="117" cy="60" rx="14" ry="26" fill="#E8B65A" transform="rotate(-54 117 60)"/>
            <circle cx="100" cy="50" r="9" fill="#FFFFFF"/>
            <circle cx="100" cy="50" r="5.5" fill="#1A6B5E"/>
            <text x="100" y="92" fontFamily="'Plus Jakarta Sans', system-ui, sans-serif" fontSize="23" fontWeight="600" letterSpacing="-0.5" fill="#1A2420" textAnchor="middle">mysa</text>
            <text x="100" y="114" fontFamily="'Plus Jakarta Sans', system-ui, sans-serif" fontSize="17" fontWeight="300" letterSpacing="2" fill="#2D9E8F" textAnchor="middle">desk</text>
          </svg>
        </div>

        <div style={styles.divider} />

        <p style={styles.heading}>Welcome back</p>
        <p style={styles.subheading}>Sign in to your clinic account</p>

        {error && (
          <div style={styles.error}>
            <span>⚠️</span> {error}
          </div>
        )}

        {resetSent && (
          <div style={styles.success}>
            ✅ Password reset email sent. Check your inbox.
          </div>
        )}

        <form onSubmit={handleLogin} noValidate>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="email">Email address</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="doctor@mysaderma.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onFocus={() => setFocused('email')}
              onBlur={() => setFocused(null)}
              style={inputStyle('email')}
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onFocus={() => setFocused('password')}
              onBlur={() => setFocused(null)}
              style={inputStyle('password')}
              required
            />
          </div>

          <div style={styles.forgotRow}>
            <button
              type="button"
              style={styles.forgotLink}
              onClick={handleForgotPassword}
              disabled={loading}
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            style={loading ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
            disabled={loading}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div style={styles.footer}>
          Mysa Desk · Clinic Management System
        </div>
      </div>
    </div>
  )
}
